// external imports
import React, { useContext, useEffect, useState, useRef } from 'react';
import { Tooltip } from 'react-tooltip';
import { Row } from 'reactstrap';
import Skeleton from 'react-loading-skeleton';
import { ClipLoader } from 'react-spinners';
import toast from 'react-hot-toast';

// local imports
import exportConfig from '../../config';
import {
	CapAlreadyQCdTT,
	CapQCdBeforeTT,
	CapQCdChangingStateTT,
	PreQCTT,
	CapQCdAgreeingTT,
	CapQCdDisagreeTT
} from './QCingFormTTs';

// custom hooks, services, contexts
import { pipelineContext } from '../../contexts/pipelineContext';

// data imports
import { commentsData } from '../../data/captureCommentsExample';

const QCingFormSmelter = ({
	onQCSubmit,
	currentCap,
	imageData,
	userSelectedQCState,
	qcState,
	selectedRadio,
	setDisagreeReason,
	disagreeReason,
	userHasSelected,
	skipCapture,
	imageDataError,
	loading,
	postCaptureStateSmelters,
	backCapture,
	user
}) => {
	/**
	 * renders QC Smelter Form to select Active/Inactive/Invalid
	 *
	 * 	- sets default disagree reason
	 * 	- determines if cap is/isn't QC'd, determines if user agreeing/disagreeing with Pre QC
	 *	- renders all parts of form
	 */

	const toastIdRef = useRef(null);

	const { currentCaptureIndex, capsInPipeline, qcPerc, qcType } =
		useContext(pipelineContext);

	const [capNotQCdAgree, setCapNotQCdAgree] = useState(false);
	const [capNotQCdDisagree, setCapNotQCdDisagree] = useState(false);
	const [capQCdAgree, setCapQCdAgree] = useState(false);
	const [capQCdDisagree, setCapQCdDisagree] = useState(false);

	useEffect(() => {
		/**
		 * determines if user is QCing or ReQCing
		 * and if agreeing/disagreeing
		 * these conditions change the form options
		 */

		if (!currentCap) return;

		const isQCd =
			typeof currentCap.state === 'number' &&
			typeof currentCap.qc_date === 'string';
		const isReQCd =
			typeof currentCap.state === 'number' &&
			typeof currentCap.qc_date === 'string' &&
			currentCap.hasOwnProperty('reQCState');

		let isAgree;

		// compare to initial state if NOT QCd
		if (!isQCd) {
			isAgree =
				selectedRadio !== undefined &&
				selectedRadio === currentCap.initial_state;
			// compare to state if QCd but not ReQCd
		} else if (isQCd && !isReQCd) {
			isAgree =
				selectedRadio !== undefined && selectedRadio === currentCap.state;

			// compare to reQC state if ReQCd
		} else if (isQCd && isReQCd) {
			isAgree =
				selectedRadio !== undefined && selectedRadio === currentCap.reQCState;
		}

		console.log('isReQCd', isReQCd);
		console.log('isAgree', isAgree);
		console.log('isQCd', isQCd);

		// Reset all flags first
		setCapNotQCdAgree(false);
		setCapNotQCdDisagree(false);
		setCapQCdAgree(false);
		setCapQCdDisagree(false);

		// Set the correct flag
		if (!isQCd && isAgree) {
			setCapNotQCdAgree(true);
		} else if (!isQCd && !isAgree) {
			setCapNotQCdDisagree(true);
		} else if (isQCd && isAgree) {
			setCapQCdAgree(true);
		} else if (isQCd && !isAgree) {
			setCapQCdDisagree(true);
		}
	}, [currentCap, selectedRadio]);

	useEffect(() => {
		/**
		 * sets the disagree reason
		 * if user changes state to disagree with Pre-QC, set a default value
		 * 0 for active/inactive to invalid
		 * 2 for inactive/invalid to active + use smoke
		 * or null if user agrees with Pre-QC
		 */

		if (currentCap.initial_state !== qcState) {
			if (
				currentCap &&
				imageData &&
				currentCap.initial_state !== -1 &&
				qcState === -1
			) {
				setDisagreeReason(0);
			} else if (
				currentCap &&
				imageData &&
				currentCap.initial_state !== 1 &&
				currentCap.use_smoke &&
				qcState === 1
			) {
				setDisagreeReason(2);
			} else {
				setDisagreeReason(null);
			}
		} else {
			setDisagreeReason(null);
		}
	}, [currentCap, imageData, qcState, userSelectedQCState]);

	const renderQCFormTable = () => {
		// return radio button choices for setting QC state
		// 1st row renders 'Pre-QC' above relevant initial_state
		// 2nd row renders radio buttons active/inactive/invalid
		// 3rd row render user's selected QC state

		const choicesDisabled = !imageData || currentCap.hasOwnProperty('error');

		return (
			<table
				id="QCFormTable"
				className="table"
				style={{
					borderCollapse: 'separate',
					borderSpacing: '0',
					padding: '0.25rem'
				}}
			>
				{/* pre qc values */}
				<tr>
					<td>
						{currentCap.initial_state === 1 && (
							<p className="my-0 py-0" data-tooltip-id="preQCvalue">
								Pre-QC
							</p>
						)}
					</td>
					<td>
						{currentCap.initial_state === 0 && (
							<p className="my-0 py-0" data-tooltip-id="preQCvalue">
								Pre-QC
							</p>
						)}
					</td>
					<td>
						{currentCap.initial_state === -1 && (
							<p className="my-0 py-0" data-tooltip-id="preQCvalue">
								Pre-QC
							</p>
						)}
					</td>
				</tr>

				{/* user to select QC state */}
				<tr>
					<td>
						<span class="r-pill__item activePill">
							{currentCap && userSelectedQCState ? (
								<>
									<input
										type="radio"
										id="qc-state-active"
										name="daily-qc-state"
										className="r-pill__input"
										value={1}
										checked={qcState === 1}
										onClick={() =>
											!choicesDisabled ? userHasSelected(1) : null
										}
									></input>
									<label for="qc-state-active">Active</label>
								</>
							) : (
								<>
									<input
										type="radio"
										id="qc-state-active"
										name="daily-qc-state"
										className="r-pill__input"
										value={1}
										checked={selectedRadio === 1}
										onClick={() =>
											!choicesDisabled ? userHasSelected(1) : null
										}
									></input>
									<label for="qc-state-active">Active</label>
								</>
							)}
						</span>
					</td>
					<td>
						<span class="r-pill__item inactivePill">
							{currentCap && userSelectedQCState ? (
								<>
									<input
										type="radio"
										id="qc-state-inactive"
										name="daily-qc-state"
										className="r-pill__input"
										value={0}
										checked={qcState === 0}
										onClick={() =>
											!choicesDisabled ? userHasSelected(0) : null
										}
									></input>
									<label for="qc-state-inactive">Inactive</label>
								</>
							) : (
								<>
									<input
										type="radio"
										id="qc-state-inactive"
										name="daily-qc-state"
										className="r-pill__input"
										value={0}
										checked={selectedRadio === 0}
										onClick={() =>
											!choicesDisabled ? userHasSelected(0) : null
										}
									></input>
									<label for="qc-state-inactive">Inactive</label>
								</>
							)}
						</span>
					</td>
					<td>
						<span class="r-pill__item invalidPill">
							{currentCap && userSelectedQCState ? (
								<>
									<input
										type="radio"
										id="qc-state-invalid"
										name="daily-qc-state"
										className="r-pill__input"
										value={-1}
										checked={qcState === -1}
										onClick={() =>
											!choicesDisabled ? userHasSelected(-1) : null
										}
									></input>
									<label for="qc-state-invalid">Invalid</label>
								</>
							) : (
								<>
									<input
										type="radio"
										id="qc-state-invalid"
										name="daily-qc-state"
										className="r-pill__input"
										value={-1}
										checked={selectedRadio === -1}
										onClick={() =>
											!choicesDisabled ? userHasSelected(-1) : null
										}
									></input>
									<label for="qc-state-invalid">Invalid</label>
								</>
							)}
						</span>
					</td>
				</tr>

				{/* cap QC'd */}
				{currentCap.qc_state === 1 && (
					<tr>
						<td>
							{currentCap.state === 1 && !loading && (
								<p className="my-0 py-0" data-tooltip-id="QCdBeforevalue">
									QC
								</p>
							)}
						</td>
						<td>
							{currentCap.state === 0 && !loading && (
								<p className="my-0 py-0" data-tooltip-id="QCdBeforevalue">
									QC
								</p>
							)}
						</td>
						<td>
							{currentCap.state === -1 && !loading && (
								<p className="my-0 py-0" data-tooltip-id="QCdBeforevalue">
									QC
								</p>
							)}
						</td>
					</tr>
				)}

				{/* user's selected state */}
				<tr>
					<td>
						{currentCap.newQC === 1 && !loading && (
							<p className="my-0 py-0" data-tooltip-id="QCvalue">
								QC
							</p>
						)}
					</td>
					<td>
						{currentCap.newQC === 0 && !loading && (
							<p className="my-0 py-0" data-tooltip-id="QCvalue">
								QC
							</p>
						)}
					</td>
					<td>
						{currentCap.newQC === -1 && !loading && (
							<p className="my-0 py-0" data-tooltip-id="QCvalue">
								QC
							</p>
						)}
					</td>
				</tr>
			</table>
		);
	};

	const renderDisagreeReason = () => {
		/**
		 * renders the disagree reason radio buttons
		 */

		if (loading) return;

		// console.log(currentCap.initial_state)
		// console.log(qcState)

		// currentCap exists, imageData fetched successfully
		// user is disagreeing (set state != preQC state)
		// changing from active/inactive to invalid
		// Choices: cloud cover/out of swath
		if (
			currentCap &&
			imageData &&
			currentCap.initial_state !== qcState &&
			currentCap.initial_state !== -1 &&
			qcState === -1
		) {
			return (
				<div className="row mb-2">
					{!currentCap.hasOwnProperty('newQC') ? (
						<h6 className="my-2 ps-4 text-center text-secondary">
							Select reason for disagreeing with pre-QC state
						</h6>
					) : (
						<p className="text-center text-secondary">
							Capture QC'd: reason for changing answer
						</p>
					)}

					<div className="col d-flex flex-column justify-content-center">
						<table style={{ width: 'auto', marginLeft: '25%' }}>
							<tr>
								<td>
									{' '}
									<input
										className="form-check-input qcing-extra-reason"
										type="radio"
										id="site-cloud-covered"
										name="qc-extra-questions"
										checked={disagreeReason === 0}
										onClick={() => setDisagreeReason(0)}
										//defaultChecked={true}
									></input>
								</td>
								<td>
									<label className="ps-2 pe-4" for="site-cloud-covered">
										Site cloud covered
									</label>
								</td>
							</tr>

							<tr>
								<td>
									<input
										className="form-check-input qcing-extra-reason"
										type="radio"
										id="site-out-of-swath"
										name="qc-extra-questions"
										value={1}
										checked={disagreeReason === 1}
										onClick={() => setDisagreeReason(1)}
									></input>
								</td>
								<td>
									{' '}
									<label className="ps-2" for="site-out-of-swath">
										Site out of swath
									</label>
								</td>
							</tr>
						</table>
					</div>
				</div>
			);

			// currentCap exists, imageData fetched successfully
			// user is disagreeing (set state != preQC state)
			// changing from inactive/invalid to active
			// site uses smoke
			// Choices: hotspots visible/smoke visible/both visible
		} else if (
			currentCap &&
			imageData &&
			currentCap.initial_state !== qcState &&
			currentCap.initial_state !== 1 &&
			currentCap.use_smoke &&
			qcState === 1
		) {
			return (
				<div className="row mb-2">
					{!currentCap.hasOwnProperty('newQC') ? (
						<h6 className="my-2 ps-4 text-center text-secondary">
							Select reason for disagreeing with pre-QC state
						</h6>
					) : (
						<p className="text-center text-secondary">
							Capture QC'd: reason for changing answer
						</p>
					)}
					<div className="col d-flex justify-content-center">
						<div className="mb-2">
							<div>
								<input
									className="form-check-input qcing-extra-reason"
									type="radio"
									id="hotspots-visible"
									name="qc-extra-questions"
									value={2}
									checked={disagreeReason === 2}
									onClick={() => setDisagreeReason(2)}
								></input>

								<label className="ps-2 pe-4" for="hotspots-visible">
									Hotspots visible
								</label>
							</div>

							<div>
								<input
									className="form-check-input qcing-extra-reason"
									type="radio"
									id="smoke-visible"
									name="qc-extra-questions"
									value={3}
									checked={disagreeReason === 3}
									onClick={() => setDisagreeReason(3)}
								></input>

								<label className="ps-2" for="smoke-visible">
									Smoke visible
								</label>
							</div>

							<div>
								<input
									className="form-check-input qcing-extra-reason"
									type="radio"
									id="hotspots-smoke-visible"
									name="qc-extra-questions"
									value={4}
									checked={disagreeReason === 4}
									onClick={() => setDisagreeReason(4)}
								></input>

								<label className="ps-2 pe-4" for="hotspots-smoke-visible">
									Hotspots and smoke visible
								</label>
							</div>
						</div>
					</div>
				</div>
			);
		} else {
			return <></>;
		}
	};

	const renderActiveInactiveWarning = () => {
		/**
		 * renders a warning for change inactive to active
		 */

		if (currentCap && currentCap.working === 0 && selectedRadio === 1) {
			return (
				<p className="text-warning text-center">
					<i className="fa-solid fa-triangle-exclamation pe-2"></i>You are
					setting the capture to active but the smelter is expected to be
					inactive. Do you want to check again?
				</p>
			);
		}
	};

	const renderQCInProgressModalBtn = () => {
		/**
		 * renders button to open QC Progress Modal
		 */

		return (
			<>
				<p className="my-0 py-0">
					<small>
						({currentCaptureIndex + 1}/{capsInPipeline.length})
					</small>{' '}
					<i
						type="button"
						data-bs-toggle="modal"
						data-bs-target="#exampleModal"
						className="interact ms-1 fa-solid fa-bars-progress fa-md"
						id="sentinel-hub-icon"
						data-tooltip-id="qc-progress-icon-TT"
					></i>
				</p>
				<Tooltip id="qc-progress-icon-TT" place="top" effect="solid">
					View progress of QC
				</Tooltip>
			</>
		);
	};

	const renderBackCaptureBtn = () => {
		return (
			<>
				<button
					className="btn interactButtonSecondary mt-2 mx-0 my-0"
					id="QCFormSmelterBackCaptureButton"
					onClick={(e) => backCapture(e)}
					disabled={capsInPipeline.length <= 1 || currentCaptureIndex === 0}
					data-tooltip-id="back-cap-button"
				>
					Back
				</button>
				{capsInPipeline.length > 1 && (
					<Tooltip id="back-cap-button" place="top" effect="solid">
						Go back 1 capture, move to capture {currentCaptureIndex}/
						{capsInPipeline.length}
					</Tooltip>
				)}
			</>
		);
	};

	const renderSkipCaptureBtn = () => {
		/**
		 * render Skip Capture button
		 *
		 * if not all caps are QC'd, enable button
		 * if all caps are QC'd, disable button
		 */

		if (
			capsInPipeline.filter((cap) => cap.hasOwnProperty('beenQCd')).length !==
			capsInPipeline.length
		) {
			//console.log("hello 1")
			// SV3-173 - Re-QC

			return (
				<>
					<button
						className="btn interactButtonSecondary mt-2 mx-0 my-0"
						id="QCFormSmelterSkipButton"
						onClick={(e) => skipCapture(e)}
						disabled={capsInPipeline.length <= 1}
						data-tooltip-id="skip-cap-button"
					>
						Skip
					</button>
					{capsInPipeline.length > 1 && (
						<Tooltip id="skip-cap-button" place="top" effect="solid">
							Skip current capture, move to capture{' '}
							{((currentCaptureIndex + 1) % capsInPipeline.length) + 1}/
							{capsInPipeline.length}
						</Tooltip>
					)}
				</>
			);
		} else {
			//console.log("hello 2")
			return (
				<button
					className="btn interactButtonSecondary mt-2 mx-0 my-0"
					id="QCFormSmelterSkipButton"
					style={{ width: '100%' }}
					disabled
				>
					Skip
				</button>
			);
		}
	};

	const renderQCBtnLoading = () => {
		/**
		 * render QC button in a loading state
		 * for when data is being fetched/posted
		 * to prevent user from continuing with QC when data hasn't fully fetched
		 */

		return (
			<div className="col-12 order-1 order-md-3 col-md-6 d-flex flex-column align-items-center">
				<button
					className="btn interactButton mt-2 mx-0 my-0"
					style={{ width: '160px' }}
					disabled
				>
					Loading .. <ClipLoader margin={3} size={18} color="white" />
				</button>
			</div>
		);
	};

	const renderQCBtnError = () => {
		/**
		 * render QC button when data was not posted correctly
		 * skips to next capture
		 * does not allow to QC capture
		 */

		return (
			<div>
				<button
					className="btn interactButton mt-2 mx-0 my-0"
					style={{ width: '160px' }}
					data-tooltip-id="errorQCingCapture"
					onClick={(e) => skipCapture(e)}
				>
					<i class="fa-solid fa-arrow-right fa-xs ps-2"></i>
				</button>
				<Tooltip id="errorQCingCapture" place="top" effect="solid">
					Image not QC'd properly: move to next capture
				</Tooltip>
			</div>
		);
	};

	const renderQCBtnCapQCdNotChanging = () => {
		/**
		 * render QC button when
		 * current cap already QC'd, agreeing with Pre QC
		 *
		 * if all caps are QC'd, open Finish QC modal
		 * if not all caps are QC'd, skip capture
		 */

		if (parseInt(qcPerc) === 100) {
			return (
				<>
					<button
						className="btn interactButton mt-2 mx-0 my-0"
						style={{ width: '160px' }}
						data-tooltip-id="moveToUploadTT"
						data-bs-toggle="modal"
						data-bs-target="#exampleModal"
						onClick={(e) => e.preventDefault()}
					>
						Finish QCing <i class="ms-2 fa-solid fa-circle-check"></i>
					</button>
					<Tooltip id="moveToUploadTT" place="top" effect="solid">
						review captures and move to upload stage
					</Tooltip>
				</>
			);
		} else {
			return (
				<>
					<button
						className="btn interactButton mt-2 mx-0 my-0"
						style={{ width: '160px' }}
						data-tooltip-id="captureAlreadyQCd2"
						onClick={(e) => skipCapture(e)}
					>
						<i class="fa-solid fa-arrow-right fa-xs ps-2"></i>
					</button>
					<Tooltip id="captureAlreadyQCd2" place="top" effect="solid">
						capture already QC'd: no change
					</Tooltip>
				</>
			);
		}
	};

	const renderQCBtnCapQCdChanging = () => {
		/**
		 * render QC button when
		 * current cap already QC'd, disagreeing with Pre QC
		 *
		 * if all caps are QC'd, open Finish QC modal
		 * if not all caps are QC'd, allow QC change state
		 */

		if (parseInt(qcPerc) === 100) {
			return (
				<button
					className="btn interactButton mt-2 mx-0 my-0"
					style={{ width: '160px' }}
					data-tooltip-id="captureAlreadyQCd3"
					data-bs-toggle="modal"
					data-bs-target="#exampleModal"
					onClick={(e) => e.preventDefault()}
				>
					<i class="fa-solid fa-arrow-right fa-xs ps-2"></i>
				</button>
			);
		} else {
			return (
				<>
					<button
						className="btn interactButton mt-2 mx-0 my-0"
						style={{ width: '160px' }}
						data-tooltip-id="captureAlreadyQCd3"
					>
						Set to{' '}
						<strong>
							{exportConfig.qcStates.find(
								(qstate) => qstate.stateInt === selectedRadio
							)?.stateString ?? 'No state selected'}
						</strong>{' '}
						<i class="fa-solid fa-arrow-right fa-xs ps-2"></i>
					</button>
					<CapQCdChangingStateTT selectedRadio={selectedRadio} />
				</>
			);
		}
	};

	const renderQCBtnCapNotQCdAgree = () => {
		/**
		 * render QC button when
		 * current cap not QC'd, agreeing with Pre QC
		 *
		 * if all caps are QC'd, open Finish QC modal
		 * if not all caps are QC'd, allow QC with same state
		 */

		if (parseInt(qcPerc) === 100) {
			return (
				<button
					className="btn interactButton mt-2 mx-0 my-0"
					style={{ width: '160px' }}
					data-tooltip-id="captureAlreadyQCd3"
					data-bs-toggle="modal"
					data-bs-target="#exampleModal"
					onClick={(e) => e.preventDefault()}
				>
					<i class="fa-solid fa-arrow-right fa-xs ps-2"></i>
				</button>
			);
		} else {
			return (
				<>
					<button
						className="btn interactButton mt-2 mx-0 my-0"
						style={{ width: '160px' }}
						data-tooltip-id="setQCagree"
					>
						Set to{' '}
						<strong>
							{
								exportConfig.qcStates.filter(
									(qstate) => qstate.stateInt === currentCap.initial_state
								)[0].stateString
							}
						</strong>{' '}
						<i class="fa-solid fa-arrow-right fa-xs ps-2"></i>
					</button>

					<CapQCdAgreeingTT selectedRadio={selectedRadio} />
				</>
			);
		}
	};

	const renderQCBtnCapNotQCdDisagree = () => {
		/**
		 * render QC button when
		 * current cap not QC'd, disagreeing with Pre QC
		 *
		 * if all caps are QC'd, open Finish QC modal
		 * if not all caps are QC'd, allow QC changing state
		 */

		if (parseInt(qcPerc) === 100) {
			return (
				<button
					className="btn interactButton mt-2 mx-0 my-0"
					style={{ width: '160px' }}
					data-tooltip-id="captureAlreadyQCd3"
					data-bs-toggle="modal"
					data-bs-target="#exampleModal"
					onClick={(e) => e.preventDefault()}
				>
					<i class="fa-solid fa-arrow-right fa-xs ps-2"></i>
				</button>
			);
		} else {
			return (
				<>
					<button
						className="btn interactButton mt-2 mx-0 my-0"
						style={{ width: '160px' }}
						data-tooltip-id="setQCdisagree"
					>
						Set to{' '}
						<strong>
							{exportConfig.qcStates.find(
								(qstate) => qstate.stateInt === selectedRadio
							)?.stateString ?? 'No state selected'}
						</strong>
						<i class="fa-solid fa-arrow-right fa-xs ps-2"></i>
					</button>

					<CapQCdDisagreeTT currentCap={currentCap} />
				</>
			);
		}
	};

	const renderCommentsIconBtn = () => {
		return (
			<>
				<i
					data-bs-toggle="modal"
					data-bs-target="#viewCaptureCommentsModal"
					className="fa-solid fa-comments ms-3"
					data-tooltip-id={'captureCommentsTT'}
					style={{
						color:
							commentsData.length > 0
								? 'rgb(80, 145, 39)'
								: 'rgba(111, 111, 111, 1)'
					}}
				></i>
				{commentsData.length > 0 && <sup>{commentsData.length}</sup>}
				<Tooltip id="captureCommentsTT" place="top" effect="solid">
					{commentsData.length > 0
						? `View comments (${commentsData.length}) for this capture`
						: 'This capture has no comment history'}
				</Tooltip>
				
			</>
		);
	};

	return (
		<div style={{ height: '100%' }}>
			<form
				className="d-flex flex-column"
				onSubmit={(e) => onQCSubmit(e)}
				style={{ minHeight: '100%' }}
				id="QC-smelter-form"
			>
				<fieldset
					className="form-group p-3 border rounded-3 bg-white"
					style={{ minHeight: '450px', height: '100%' }}
				>
					<legend className="float-none w-auto px-3 my-0 fs-6">
						QC Capture
					</legend>
					<div
						style={{ position: 'relative', width: '100%', minHeight: '450px' }}
					>
						{/* no current capture */}
						{!currentCap ? (
							<Skeleton width={100} height={100} />
						) : // image data not fetched properly
						!imageData ||
						  currentCap.hasOwnProperty('newQC') ||
						  currentCap.hasOwnProperty('error') ? (
							<>
								<Row>
									{(currentCap.hasOwnProperty('newQC') && qcType !== 'Re-QC') ||
									qcType === 'Re-QC' ? (
										<h6 className="my-2 ps-4 text-success text-center">
											Capture QC'd
										</h6>
									) : (
										<h6 className="my-2 ps-4 text-secondary">
											Select the QC state of the capture
										</h6>
									)}

									{renderQCFormTable()}

									<PreQCTT currentCap={currentCap} />

									{currentCap.newQC != null &&
										currentCap.newQC !== 'failed' && (
											<CapAlreadyQCdTT currentCap={currentCap} />
										)}
								</Row>
							</>
						) : (
							// all data fetched correctly
							<>
								<Row>
									{currentCap.hasOwnProperty('newQC') && qcType !== 'Re-QC' ? (
										<h6 className="my-2 ps-4 text-success text-center">
											Capture QC'd
										</h6>
									) : qcType === 'Re-QC' ? (
										<h6 className="my-2 ps-4 text-secondary">
											Select the Re-QC state of the capture
										</h6>
									) : (
										<h6 className="my-2 ps-4 text-secondary">
											Select the QC state of the capture
										</h6>
									)}

									{currentCap.hasOwnProperty('error') && (
										<p className="text-warning">
											This capture was not QC'd properly.
										</p>
									)}

									{renderQCFormTable()}

									{/* TOOLTIPS */}
									<PreQCTT currentCap={currentCap} />

									{currentCap.qc_state === 1 && (
										<CapQCdBeforeTT currentCap={currentCap} />
									)}

									{currentCap.newQC != null &&
										currentCap.newQC !== 'failed' && (
											<CapAlreadyQCdTT currentCap={currentCap} />
										)}
								</Row>
							</>
						)}

						{renderDisagreeReason()}

						{renderActiveInactiveWarning()}

						{/* BUTTONS */}
						{currentCap && (
							<div
								className="position-absolute bottom-0 end-0"
								style={{ width: '100%' }}
							>
								<div className="row">
									<div className="col d-flex justify-content-end pt-1">
										<div style={{ width: '100%' }}>
											{/* if data did not arrive */}
											{imageDataError ? (
												<div className="row">
													<div className="col-6 order-2 order-lg-2  d-flex justify-content-center align-items-center"></div>
													<div className="col-6 order-3 order-lg-3  d-flex justify-content-center align-items-center">
														<button
															className="btn interactButton mt-2 mx-0 my-0"
															style={{ width: '100%' }}
															onClick={(e) => skipCapture(e)}
															data-tooltip-id="skipCapTT"
														>
															Skip
														</button>
														{currentCaptureIndex !==
															capsInPipeline.length - 1 && (
															<Tooltip
																id="skipCapTT"
																place="top"
																effect="solid"
															>
																Skip current capture, move to capture{' '}
																{currentCaptureIndex + 2}/
																{capsInPipeline.length}
															</Tooltip>
														)}

														{currentCaptureIndex ===
															capsInPipeline.length - 1 && (
															<Tooltip
																id="skipCapTT"
																place="top"
																effect="solid"
															>
																Skip current capture, move to capture 1/
																{capsInPipeline.length}
															</Tooltip>
														)}
													</div>
													<div className="col-12 order-1 d-flex flex-column align-items-center ">
														<button
															className="btn interactButtonSecondary mt-2 mx-0 my-0"
															style={{ width: '160px' }}
															disabled
														>
															{' '}
															<i class="fa-solid fa-arrow-right fa-xs ps-2"></i>
														</button>
													</div>
												</div>
											) : (
												// data fetched successfully

												<>
													{/* <div className="row mx-0 px-0">
														<div className="col-4 order-2 order-lg-2 d-flex justify-content-center align-items-center">
															{renderQCInProgressModalBtn()}
														</div>

														<div className="col-4 order-2 order-lg-2 d-flex justify-content-center align-items-center">
															<i
																data-bs-toggle="modal"
																data-bs-target="#viewCaptureCommentsModal"
																className="fa-solid fa-comments"
																data-tooltip-id={"captureCommentsTT"}
																style={{
																	color:
																		commentsData.length > 0
																			? 'rgb(80, 145, 39)'
																			: 'rgba(111, 111, 111, 1)'
																}}
															></i>

															{commentsData.length > 0 && (
																<sup>{commentsData.length}</sup>
															)}

															<Tooltip
																id="captureCommentsTT"
																place="top"
																effect="solid"
															>
																{commentsData.length > 0 ? 'View comments for this capture': 'This capture has no comment history'}
															</Tooltip>
														</div>

														<div className="col-4 order-3 order-lg-3  d-flex justify-content-center align-items-center">
															{renderSkipCaptureBtn()}
														</div>

														<div className="col-12 order-1 d-flex flex-column align-items-center ">
															{loading ? (
																renderQCBtnLoading()
															) : postCaptureStateSmelters.error &&
															  currentCap.hasOwnProperty('error') ? (
																renderQCBtnError()
															) : capQCdAgree ? (
																renderQCBtnCapQCdNotChanging()
															) : capQCdDisagree ? (
																renderQCBtnCapQCdChanging()
															) : capNotQCdAgree ? (
																renderQCBtnCapNotQCdAgree()
															) : capNotQCdDisagree ? (
																renderQCBtnCapNotQCdDisagree()
															) : (
																<></>
															)}
														</div>
													</div> */}

													<div className="row mx-0 px-0">
														<div className="col-12 order-1 d-flex flex-column align-items-center ">
															{loading ? (
																renderQCBtnLoading()
															) : postCaptureStateSmelters.error &&
															  currentCap.hasOwnProperty('error') ? (
																renderQCBtnError()
															) : capQCdAgree ? (
																renderQCBtnCapQCdNotChanging()
															) : capQCdDisagree ? (
																renderQCBtnCapQCdChanging()
															) : capNotQCdAgree ? (
																renderQCBtnCapNotQCdAgree()
															) : capNotQCdDisagree ? (
																renderQCBtnCapNotQCdDisagree()
															) : (
																<></>
															)}
														</div>

														<div className="col-4 order-2 order-lg-1 d-flex justify-content-center align-items-center">
															{renderBackCaptureBtn()}
														</div>

														<div className="col-4 order-3 order-lg-2 d-flex justify-content-center align-items-center">
															{renderQCInProgressModalBtn()}

															{/* {renderCommentsIconBtn()} */}
														</div>

														<div className="col-4 order-4 order-lg-3  d-flex justify-content-center align-items-center">
															{renderSkipCaptureBtn()}
														</div>
													</div>
												</>

												// <>
												// 	<div className="row mx-0 px-0">
												// 		<div className="col-6 order-2 order-lg-2 d-flex justify-content-center align-items-center">
												// 			{renderQCInProgressModalBtn()}
												// 		</div>

												// 		<div className="col-6 order-3 order-lg-3  d-flex justify-content-center align-items-center">
												// 			{renderSkipCaptureBtn()}
												// 		</div>

												// 		<div className="col-12 order-1 d-flex flex-column align-items-center ">
												// 			{loading ? (
												// 				renderQCBtnLoading()
												// 			) : postCaptureStateSmelters.error &&
												// 			  currentCap.hasOwnProperty('error') ? (
												// 				renderQCBtnError()
												// 			) : capQCdAgree ? (
												// 				renderQCBtnCapQCdNotChanging()
												// 			) : capQCdDisagree ? (
												// 				renderQCBtnCapQCdChanging()
												// 			) : capNotQCdAgree ? (
												// 				renderQCBtnCapNotQCdAgree()
												// 			) : capNotQCdDisagree ? (
												// 				renderQCBtnCapNotQCdDisagree()
												// 			) : (
												// 				<></>
												// 			)}
												// 		</div>
												// 	</div>
												// </>
											)}
										</div>
									</div>{' '}
								</div>
							</div>
						)}
					</div>
				</fieldset>
			</form>
		</div>
	);
};

export default QCingFormSmelter;
